/*
 * assets/js/src/form-chips.js — výběr programů jako čipy.
 *
 * ---------------------------------------------------------------------
 * VRSTVA NAD NATIVNÍM SELECTEM, NE NÁHRADA ZA NĚJ
 * ---------------------------------------------------------------------
 * Návrh má `.chipsel` jako mockup BEZ formulářové sémantiky: žádný
 * `name`, žádná hodnota, seznam programů zadrátovaný v inline skriptu.
 * Postavit ho doslova by znamenalo formulář, který nemá co odeslat.
 *
 * Modul proto nechává v DOM nativní `<select multiple>` — ten je zdroj
 * pravdy — a staví nad ním viditelnou vrstvu. Důsledky, kvůli kterým to
 * takhle je:
 *   - payload zůstává `…SelectedEvent[]` bit po bitu (kontrakt §0),
 *   - bez JS zůstane vidět nativní select a dá se použít,
 *   - `form-preselect` funguje dál: sahá na select, ne na čipy.
 *
 * Select se neschovává `display: none` — to by ho vyřadilo z pořadí
 * tabulátoru a klávesnicová cesta by zmizela. Zůstává dostupný a vizuálně
 * skrytý; čipy jsou `aria-hidden` a jsou to ovládací prvky navíc, ne
 * jediná cesta.
 *
 * ---------------------------------------------------------------------
 * CO MODUL NENESE
 * ---------------------------------------------------------------------
 * Žádný text. Popisky tlačítek čte z markupu (`data-za-pridat`,
 * `data-za-odebrat`), protože texty patří do registru, ne do JS —
 * jinak by EN mutace zůstala česky a editor by o nich nevěděl.
 */

const ROOT = '.list-box select[multiple].no-selectric';

/**
 * Postaví čipovou vrstvu nad jedním selectem.
 *
 * @param {HTMLSelectElement} select Nativní select.
 */
function postav(select) {
	const box = select.closest('.list-box');
	if (!box) return false;

	const textPridat = box.getAttribute('data-za-pridat') || '+';
	const textOdebrat = box.getAttribute('data-za-odebrat') || '×';

	const obal = document.createElement('div');
	obal.className = 'chipsel';

	const cipy = document.createElement('div');
	cipy.className = 'chips';

	const pridat = document.createElement('button');
	pridat.type = 'button';
	pridat.className = 'addbtn';
	pridat.setAttribute('aria-expanded', 'false');
	pridat.setAttribute('aria-label', textPridat);
	pridat.textContent = '+';

	const nabidka = document.createElement('div');
	nabidka.className = 'chipdrop';

	obal.appendChild(cipy);
	obal.appendChild(pridat);
	obal.appendChild(nabidka);
	box.appendChild(obal);
	select.classList.add('za-select-skryty');

	/** Možnosti bez prázdné úvodní položky — ta je jen popisek. */
	const moznosti = Array.prototype.filter.call(select.options, (o) => '' !== o.value);

	function vykresli() {
		cipy.textContent = '';
		for (const opt of moznosti) {
			if (!opt.selected) continue;
			const cip = document.createElement('span');
			cip.className = 'chip';
			cip.appendChild(document.createTextNode(opt.value));
			const pryc = document.createElement('button');
			pryc.type = 'button';
			pryc.setAttribute('aria-label', textOdebrat + ' ' + opt.value);
			pryc.textContent = '×';
			pryc.addEventListener('click', () => {
				opt.selected = false;
				zmena();
			});
			cip.appendChild(pryc);
			cipy.appendChild(cip);
		}

		nabidka.textContent = '';
		for (const opt of moznosti) {
			const tl = document.createElement('button');
			tl.type = 'button';
			tl.className = opt.selected ? 'on' : '';
			tl.setAttribute('aria-pressed', opt.selected ? 'true' : 'false');
			tl.textContent = (opt.selected ? '✓ ' : '') + opt.value;
			tl.addEventListener('click', () => {
				opt.selected = !opt.selected;
				zmena();
			});
			nabidka.appendChild(tl);
		}
	}

	function zmena() {
		vykresli();
		select.dispatchEvent(new window.Event('change', { bubbles: true }));
	}

	/*
	 * Změna může přijít i odjinud — `form-preselect` nastavuje hodnotu
	 * přímo na selectu. Posloucháme proto `change` a překreslujeme; bez
	 * toho by čipy tvrdily něco jiného, než co formulář odešle.
	 */
	select.addEventListener('change', vykresli);

	pridat.addEventListener('click', (e) => {
		e.stopPropagation();
		const otevreno = obal.classList.toggle('open');
		pridat.setAttribute('aria-expanded', otevreno ? 'true' : 'false');
	});

	document.addEventListener('click', (e) => {
		if (!obal.contains(e.target)) {
			obal.classList.remove('open');
			pridat.setAttribute('aria-expanded', 'false');
		}
	});

	document.addEventListener('keydown', (e) => {
		if (e.key === 'Escape' && obal.classList.contains('open')) {
			obal.classList.remove('open');
			pridat.setAttribute('aria-expanded', 'false');
			pridat.focus();
		}
	});

	vykresli();
	return true;
}

/**
 * Spustí modul. Vrací počet obsloužených selectů.
 *
 * @returns {number}
 */
export function init() {
	let n = 0;
	for (const select of document.querySelectorAll(ROOT)) {
		if (postav(select)) n += 1;
	}
	return n;
}
